import _ from 'lodash';
import { Assert } from '@cotera/utilities';

/**
 * The information needed to render a portion of a formatted EraQL program.
 *
 * The "width" of a node is the hypothetical render-width of the node if it and all
 * its descendants were rendered without wrapping.
 *
 * A "head" node represents a layer of "dependents" under it where every node in said layer
 * is wrapped if the head's "width" cannot fit on one line.
 *
 * The "dependents" of a head are all its descendents before the nearest head child.
 *
 * An ancester head needing wrapping does not imply that a descendent head needs wrapping;
 * (perhaps the nodes of the descendent head may be renderable in a single line).
 */
export type FormatNode =
  | Head
  | Text
  | Line
  | LineOrSpace
  | LineOrNothing
  | Indent
  | Nodes
  | IfWrapAncestor;

/**
 * A base portion of the EraQL program, with no further formatting needs beyond the value itself.
 *
 * (The value may contain unicode whose `.length` is longer than user-percieved width).
 */
export interface Text {
  readonly t: 'text';
  value: string;
  /**
   * The approximate user-percieved character width.
   *
   * Or, preceisely, the number of "extended grapheme clusters".
   *
   * (cached here for performance, as evaluation is O(n).)
   */
  width: number;
}

/**
 * A forced new line in the program.
 */
export interface Line {
  readonly t: 'line';
}

/**
 * If nearest parent "head" needs wraping, renders to a line, else renders to a space.
 */
export interface LineOrSpace {
  readonly t: 'lineOrSpace';
}

/**
 * If nearest parent "head" needs wrapping, renders to a line, else renders to a line.
 */
export interface LineOrNothing {
  readonly t: 'lineOrNothing';
}
/**
 * If nearest parent "head" needs wrapping, the indentation level is incremented for any
 * new line in any descendent node.
 */
export interface Indent {
  readonly t: 'indent';
  nodes: FormatNode[];
}

/**
 * A head of a group of nodes, whose content we try to fit in one line (else, its "dependents" are wrapped).
 *
 * i.e., in the else case, all of this node's descendants before the nearest head child will be wrapped.
 *
 * An ancestor "head" node needing wrapping does not imply the nodes under this head need wrapping.
 */
export interface Head {
  readonly t: 'head';
  headId: string; // unique to the document being formatted.
  nodes: FormatNode[];
}

/**
 * Simply a collection of nodes (rendered without any special handling).
 *
 * (If nearest parent "head" needs wrapping, that need is passed on to this node's
 * non-head descendants.)
 */
export interface Nodes {
  readonly t: 'nodes';
  nodes: FormatNode[];
}

/**
 * Renders node A if ancestorHeadId needs to be wrapped, else renders node B.
 *
 * In the node A case, node A (and its non-head descendents) are wrapped too.
 *
 * (Eg can be used to render a trailing comma or nothing depending on whether the head node representing a list is wrapped).
 */
export interface IfWrapAncestor {
  readonly t: 'ifWrapAncestor';
  ancestorHeadId: string;
  A: FormatNode;
  B: FormatNode;
}

export const Text = (value: string): Text => ({
  t: 'text',
  value,
  width: evaluateUnicodeWidth(value),
});
export const Line = (): Line => ({ t: 'line' });
export const LineOrSpace = (): LineOrSpace => ({ t: 'lineOrSpace' });
export const LineOrNothing = (): LineOrNothing => ({ t: 'lineOrNothing' });
export const Indent = (nodes: FormatNode[]): Indent => ({ t: 'indent', nodes });

export const Head = (headId: string, nodes: FormatNode[]): Head => ({
  t: 'head',
  headId: headId,
  nodes,
});

export const Nodes = (nodes: FormatNode[]): Nodes => ({ t: 'nodes', nodes });

export const IfWrapAncestor = (
  ancestorHeadId: string,
  A: FormatNode,
  B: FormatNode
): IfWrapAncestor => ({
  t: 'ifWrapAncestor',
  ancestorHeadId: ancestorHeadId,
  A,
  B,
});

/**
 * The width of the rendered node, if hypothetically the node and all its
 * descendents were rendered with no wrapping.
 *
 * (a performance benefit may be achieved by caching the output of this function)
 */
export function evaluateWidth(
  node: FormatNode,
  wrappedHeadIds: Set<string> = new Set()
): number {
  switch (node.t) {
    case 'text':
      return node.width;

    case 'line':
      return 0;

    case 'lineOrSpace':
      return 1;

    case 'lineOrNothing':
      return 0;

    // TODO: consider whether evaluation of children width should stop if a forced-line is reached.
    case 'nodes':
    case 'head':
    case 'indent': {
      const _1: 'head' | 'nodes' | 'indent' = node.t; // assert type
      return _.sum(
        node.nodes.map((child) => evaluateWidth(child, wrappedHeadIds))
      );
    }

    case 'ifWrapAncestor':
      return wrappedHeadIds.has(node.ancestorHeadId)
        ? evaluateWidth(node.A)
        : evaluateWidth(node.B);

    default:
      return Assert.unreachable(node);
  }
}

/**
 * Returns the user-percieved width of a unicode string.
 *
 * Precisely, it returns the number of "extended grapheme clusters" in
 * the string. This algo runs in O(n) time, so results should be cached.
 *
 * Example:
 * ```
 * const string = "🏳️‍⚧️🏳️‍🌈👩🏾‍❤️‍👨🏻";
 * const segments = new Intl.Segmenter().segment(string);
 *
 * console.log(`Intl.Segmenter length: ${[...segments].length}`);
 * // Intl.Segmenter length: 3
 *
 * console.log(`String length: ${string.length}`);
 * // String length: 24
 *
 * console.log(`Iterator length: ${[...string].length}`)
 * // Iterator length: 17
 * ```
 */
const evaluateUnicodeWidth = (value: string): number =>
  [...new Intl.Segmenter().segment(value)].length;
